import React, { Component } from 'react'
import styled from 'styled-components'

// assets
import { Check } from '@styled-icons/feather'

// icons
import { MinusSign, PlusSign } from 'components/Icons/Icons'

// utils and config
import breakpoints from 'utils/breakpoints'
import th from 'utils/themeHelper'

// Load components synchronously
import ConditionalDisplay from 'components/ConditionalDisplay/ConditionalDisplay'

const Wrapper = styled.div`
  border-radius: 4px;
  background-color: white;
  border: 1px solid ${th('borders.default')};
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.08);
  margin-bottom: ${props => (props.collapsable && props.isCollapsed ? '1em' : '2em')};

  @media (min-width: ${breakpoints.formMiniMapDisplay}) {
    margin-bottom: 2em;
    margin-right: ${props => (props.fullWidth ? '0' : '1em')};
  }
`

const CompletedIconWrapper = styled.div`
  height: 30px;
  width: 30px;
  padding: 4px;
  border-radius: 50%;
  background: ${th('secondary.base')};
  color: white;
  stroke-width: 2px;
`

const DashboardSectionBlockHeader = styled.div`
  align-items: center;
  background: ${props =>
    props.disabled
      ? th('backgrounds.mid')
      : props.displayUpgradePill
      ? th('backgrounds.dark')
      : props.hasErrorOrWarning
      ? th('borders.error')
      : th('secondary.dark')};
  color: ${props => (props.disabled ? th('text.dark') : 'white')};
  display: ${props => (props.flex ? 'flex' : 'block')};
  font-family: ${th('fonts.bold')};
  font-size: 15px;
  letter-spacing: 0.25px;
  line-height: 15px;
  overflow-x: auto;
  padding: ${props => (props.collapsable && props.isCollapsed ? '20px 15px' : '15px')};
  position: relative;

  @media screen and (min-width: 769px) {
    padding: 20px;
  }
`

const DashboardSectionBlockContent = styled.div`
  padding: 15px;

  @media screen and (min-width: 769px) {
    padding: 20px;
  }
`

const CollapsePillWrapper = styled.div`
  display: block;
  // So collapsable components have the same padding for the header section
  margin-bottom: -13.5px;
  margin-top: -13.5px;
`

const CollapsePill = styled.div`
  border: 1px solid white;
  border-radius: 3px;
  cursor: pointer;
  margin-left: 1em;
  padding: 5px;
`

const FlexHeader = styled.div`
  flex: 1;
`

const FlexHeaderPill = styled.div`
  background: ${props =>
    props.hasErrorOrWarning
      ? 'white'
      : props.disabled && props.active
      ? '#eee'
      : props.active
      ? 'white'
      : 'none'};
  color: ${props =>
    props.disabled
      ? th('backgrounds.dark')
      : props.active
      ? th('backgrounds.dark')
      : props.hasErrorOrWarning
      ? th('text.error')
      : 'white'};
  border: 1px solid
    ${props =>
      props.disabled
        ? th('backgrounds.dark')
        : props.active
        ? 'white'
        : props.hasErrorOrWarning
        ? th('text.error')
        : 'white'};
  border-radius: 3px;
  margin-right: 1em;
  padding: 8px 16px;
  white-space: nowrap;

  &:hover {
    background: white;
    border-color: ${props => (props.active ? 'none' : th('backgrounds.dark'))};
    color: ${th('backgrounds.dark')};
    cursor: pointer;
  }
`

class DashboardSectionBlock extends Component {
  state = { activeSection: null, isCollapsed: true }

  toggleCollapsedState() {
    this.setState({ isCollapsed: !this.state.isCollapsed })
  }

  componentDidMount() {
    if (this.props.defaultOpen) {
      this.setState({ isCollapsed: false })
    }
  }

  setSectionActive({ id }) {
    this.setState({ activeSection: id })
  }

  render() {
    const {
      children,
      collapsable,
      disabled,
      fullWidth,
      header,
      headers,
      id,
      isActive,
      multiOpt
    } = this.props
    const { activeSection, isCollapsed } = this.state

    const hasErrorOrWarning = this.props.hasErrorOrWarning || this.props.hasError

    if (multiOpt) {
      const activeFormSection = headers.find(h => h.id === activeSection) || headers[0]
      const activeFormSectionId = activeFormSection.id
      const activeFormFields = children.filter(c => activeFormSectionId === c.props.formSection)

      return (
        <Wrapper id={id} fullWidth={fullWidth}>
          <DashboardSectionBlockHeader disabled={disabled} flex>
            {headers.map(({ id, label }) => (
              <FlexHeaderPill
                disabled={disabled}
                active={id === activeFormSectionId}
                hasErrorOrWarning={hasErrorOrWarning}
                key={`header_${id}`}
                onClick={() => this.setSectionActive({ id })}
              >
                {label}
              </FlexHeaderPill>
            ))}
          </DashboardSectionBlockHeader>

          <DashboardSectionBlockContent>{activeFormFields}</DashboardSectionBlockContent>
        </Wrapper>
      )
    }

    if (collapsable) {
      return (
        <Wrapper id={id} collapsable isCollapsed={isCollapsed} fullWidth={fullWidth}>
          <DashboardSectionBlockHeader
            collapsable
            disabled={disabled}
            hasErrorOrWarning={hasErrorOrWarning}
            isCollapsed={isCollapsed}
            flex
          >
            <FlexHeader>{header}</FlexHeader>

            <CollapsePillWrapper>
              <CollapsePill onClick={() => this.toggleCollapsedState()}>
                <div style={{ width: '15px' }}>{isCollapsed ? <PlusSign /> : <MinusSign />}</div>
              </CollapsePill>
            </CollapsePillWrapper>
          </DashboardSectionBlockHeader>

          <ConditionalDisplay displayWhen={[!isCollapsed]}>
            <DashboardSectionBlockContent>{children}</DashboardSectionBlockContent>
          </ConditionalDisplay>
        </Wrapper>
      )
    }

    if (isActive) {
      return (
        <Wrapper id={id} fullWidth={fullWidth}>
          <DashboardSectionBlockHeader hasErrorOrWarning={hasErrorOrWarning} flex>
            <FlexHeader>{header}</FlexHeader>

            <CompletedIconWrapper>
              <Check />
            </CompletedIconWrapper>
          </DashboardSectionBlockHeader>

          <DashboardSectionBlockContent>{children}</DashboardSectionBlockContent>
        </Wrapper>
      )
    }

    return (
      <Wrapper id={id} fullWidth={fullWidth}>
        <DashboardSectionBlockHeader disabled={disabled} hasErrorOrWarning={hasErrorOrWarning} flex>
          <FlexHeader>{header}</FlexHeader>
        </DashboardSectionBlockHeader>

        {children && <DashboardSectionBlockContent>{children}</DashboardSectionBlockContent>}
      </Wrapper>
    )
  }
}

export default DashboardSectionBlock
